import { useEffect, useState, useCallback } from 'react';
import { realtimeService } from '../services/realtimeService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook for real-time entity subscriptions
 * Automatically manages subscriptions and state updates
 * 
 * @param {string} entity - Entity name (contacts, deals, customers, etc.)
 * @param {Array} initialData - Initial data array
 * @param {function} transformFn - Optional transform function for new data
 * @returns {Object} { data, setData, isConnected }
 */
export const useRealtimeEntity = (entity, initialData = [], transformFn = null) => {
  const { profile } = useAuth();
  const [data, setData] = useState(initialData);
  const [isConnected, setIsConnected] = useState(false);

  // Update data when initialData changes
  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  // Handle INSERT events
  const handleInsert = useCallback((newRecord) => {
    const transformedRecord = transformFn ? transformFn(newRecord) : newRecord;
    setData(prevData => {
      // Check if record already exists (prevent duplicates)
      const exists = prevData?.some(item => item?.id === transformedRecord?.id);
      if (exists) return prevData;
      return [transformedRecord, ...prevData];
    });
  }, [transformFn]);

  // Handle UPDATE events
  const handleUpdate = useCallback((updatedRecord) => {
    const transformedRecord = transformFn ? transformFn(updatedRecord) : updatedRecord;
    setData(prevData =>
      prevData?.map(item =>
        item?.id === transformedRecord?.id ? { ...item, ...transformedRecord } : item
      )
    );
  }, [transformFn]);

  // Handle DELETE events
  const handleDelete = useCallback((deletedRecord) => {
    setData(prevData => prevData?.filter(item => item?.id !== deletedRecord?.id));
  }, []);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!profile?.tenant_id) {
      console.warn(`[useRealtimeEntity] Cannot subscribe to ${entity}: no tenant_id`);
      return;
    }

    console.log(`[useRealtimeEntity] Subscribing to ${entity} for tenant ${profile?.tenant_id}`);
    setIsConnected(true);

    const unsubscribe = realtimeService?.subscribeToEntity(
      entity,
      profile?.tenant_id,
      {
        onInsert: handleInsert,
        onUpdate: handleUpdate,
        onDelete: handleDelete
      }
    );

    return () => {
      console.log(`[useRealtimeEntity] Unsubscribing from ${entity}`);
      setIsConnected(false);
      unsubscribe();
    };
  }, [entity, profile?.tenant_id, handleInsert, handleUpdate, handleDelete]);

  return {
    data,
    setData,
    isConnected
  };
};

export default useRealtimeEntity;
